'use client'

import React from 'react'

import { ArrowDouble } from '../../icons'
import { Container, ContainerProps, Dots, Page } from './styles'

export interface PaginationProps extends ContainerProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Current page
   */
  currentPage: number

  /**
   * Total pages
   */
  totalPages: number

  /**
   * Max visible pages (until the ellipsis is shown)
   */
  maxVisiblePages?: number

  /**
   * onChange handler
   */
  onChange: (page: number) => void
}

const PAGE_OFFSET = 1

const usePagination = (
  totalPages: number,
  currentPage: number,
  maxVisiblePages = 5,
) => {
  const shouldOmitPages = totalPages > maxVisiblePages

  // Pages without first and last page
  const pages: number[] = React.useMemo(
    () =>
      (!shouldOmitPages
        ? // Shouldn't omit pages: show all pages between first and last page
          Array.from({ length: totalPages }).map((_, i) => i + 1)
        : // Should omit pages: show pages around current page
          Array.from({ length: PAGE_OFFSET * 2 + 1 }).map((_, i) => {
            if (currentPage - PAGE_OFFSET < 1) {
              return i + 1
            }

            if (currentPage + PAGE_OFFSET > totalPages) {
              return totalPages - PAGE_OFFSET * 2 + i
            }

            return currentPage - PAGE_OFFSET + i
          })
      )
        // Remove first and last page, or 0
        .filter((page) => page !== 1 && page !== totalPages),
    [currentPage, shouldOmitPages, totalPages],
  )

  const isWithinFirstRange = currentPage < PAGE_OFFSET * 2 + 1
  const isWithinLastRange = currentPage > totalPages - PAGE_OFFSET * 2

  return {
    firstPage: totalPages > 0 && (isWithinFirstRange || isWithinLastRange),
    lastPage: totalPages > 1 && (isWithinFirstRange || isWithinLastRange),
    omittedLeft: shouldOmitPages && !isWithinFirstRange,
    omittedRight: shouldOmitPages && !isWithinLastRange,
    pages,
  }
}

/**
 * Pagination component
 */
export const Pagination = ({
  currentPage,
  totalPages,
  maxVisiblePages = 5,
  onChange,
  ...rest
}: PaginationProps) => {
  const { pages, firstPage, lastPage, omittedLeft, omittedRight } =
    usePagination(totalPages, currentPage, maxVisiblePages)

  return (
    <Container {...rest}>
      <Page
        type="button"
        onClick={() => currentPage !== 1 && onChange(1)}
        disabled={currentPage === 1}
      >
        <ArrowDouble direction="left" />
      </Page>

      {firstPage && (
        <Page
          type="button"
          onClick={() => currentPage !== 1 && onChange(1)}
          active={currentPage === 1}
        >
          1
        </Page>
      )}

      {omittedLeft && <Dots>...</Dots>}

      {pages.map((page, i) => (
        <Page
          type="button"
          key={i}
          onClick={() => page !== currentPage && onChange(page)}
          active={page === currentPage}
        >
          {page}
        </Page>
      ))}

      {omittedRight && <Dots>...</Dots>}

      {lastPage && (
        <Page
          type="button"
          onClick={() => currentPage !== totalPages && onChange(totalPages)}
          active={currentPage === totalPages}
        >
          {totalPages}
        </Page>
      )}

      <Page
        type="button"
        onClick={() => onChange(totalPages)}
        disabled={currentPage === totalPages}
      >
        <ArrowDouble />
      </Page>
    </Container>
  )
}
